import { Link } from 'react-router-dom';
import type { Drama } from '@/mocks/dramas';

interface DramaCardProps {
  drama: Drama;
  variant?: 'default' | 'wide';
}

export default function DramaCard({ drama, variant = 'default' }: DramaCardProps) {
  const statusLabel = drama.status === 'ongoing' ? '连载中' : '已完结';

  if (variant === 'wide') {
    return (
      <Link
        to={`/detail/${drama.id}`}
        className="group flex gap-4 p-3 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors"
      >
        <div className="w-24 h-32 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={drama.cover}
            alt={`${drama.title}海报`}
            title={`${drama.title} - 迷人的保姆在线观看`}
            className="w-full h-full object-top object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex flex-col justify-center min-w-0">
          <h3 className="font-semibold text-foreground-950 truncate group-hover:text-primary-700 transition-colors">
            {drama.title}
          </h3>
          <p className="text-xs text-foreground-500 mt-1 truncate">{drama.genres.join(' · ')}</p>
          <p className="text-xs text-foreground-600 mt-2">
            更新至 {drama.updatedEpisodes} 集 / 共 {drama.totalEpisodes} 集
          </p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${drama.id}`}
      className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-all duration-300 hover:-translate-y-1"
    >
      <div className="relative w-full aspect-[3/4] overflow-hidden bg-background-200">
        <img
          src={drama.cover}
          alt={`${drama.title}电视剧海报`}
          title={`${drama.title} - 迷人的保姆在线观看`}
          className="w-full h-full object-top object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <span
          className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-[11px] font-medium ${
            drama.status === 'ongoing'
              ? 'bg-accent-500 text-background-50 dark:text-foreground-950'
              : 'bg-secondary-800/90 text-background-50'
          }`}
        >
          {statusLabel}
        </span>
        {drama.featured && (
          <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[11px] font-medium bg-primary-500/90 text-background-50 dark:text-foreground-950">
            推荐
          </span>
        )}
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-primary-950/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-3">
          <span className="inline-flex items-center gap-1.5 text-xs text-background-50">
            <i className="ri-play-circle-fill text-lg"></i>
            查看详情
          </span>
        </div>
      </div>
      <div className="p-3.5">
        <h3 className="font-semibold text-sm text-foreground-950 truncate group-hover:text-primary-700 transition-colors">
          {drama.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-1 truncate">{drama.alias}</p>
        <div className="mt-2.5 flex items-center justify-between text-[11px] text-foreground-600">
          <span className="truncate">{drama.genres.slice(0, 2).join(' · ')}</span>
          <span className="font-label shrink-0 ml-2">
            更新 {drama.updatedEpisodes}/{drama.totalEpisodes}
          </span>
        </div>
      </div>
    </Link>
  );
}